// SNIGJ - My Profile Page - User can view their own profile
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import DashboardLayout from '../components/layout/DashboardLayout';
import UserProfileCard from '../components/UserProfileCard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { 
  User, Mail, Phone, MapPin, Calendar, Save, 
  Lock, Eye, EyeOff, Activity, FileText, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const MyProfilePage = () => {
  const { user, language, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [profileKey, setProfileKey] = useState(0); // Key to force re-render of profile card
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    name: '',
    phone_number: '',
    address: '',
    date_of_birth: '',
    national_id: ''
  });

  // Password change form
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    if (user) {
      setEditForm({
        name: user.name || '',
        phone_number: user.phone_number || '',
        address: user.address || '',
        date_of_birth: user.date_of_birth || '',
        national_id: user.national_id || ''
      });
      loadActivities();
    }
  }, [user]);

  const loadActivities = async () => {
    if (!user?.user_id) return;
    try {
      const response = await api.get(`/users/${user.user_id}/activity?limit=20`);
      setActivities(response.data.activities || []);
    } catch (error) {
      console.error('Failed to load activities:', error);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      setLoading(true);
      await api.put(`/users/${user.user_id}`, {
        name: editForm.name,
        phone_number: editForm.phone_number,
        address: editForm.address,
        date_of_birth: editForm.date_of_birth,
        national_id: editForm.national_id
      });
      toast.success(language === 'fr' ? 'Profil mis à jour' : 'Profile updated');
      // Refresh the profile card
      setProfileKey(prev => prev + 1);
      if (refreshUser) refreshUser();
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error(language === 'fr' ? 'Erreur de mise à jour' : 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error(language === 'fr' ? 'Les mots de passe ne correspondent pas' : 'Passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      toast.error(language === 'fr' ? 'Le mot de passe doit avoir au moins 8 caractères' : 'Password must be at least 8 characters');
      return;
    }
    
    try {
      setLoading(true);
      await api.put(`/users/${user.user_id}/password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success(language === 'fr' ? 'Mot de passe changé' : 'Password changed');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">
            {language === 'fr' ? 'Chargement...' : 'Loading...'}
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <User className="w-6 h-6" />
            {language === 'fr' ? 'Mon Profil' : 'My Profile'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {language === 'fr' 
              ? 'Consultez et modifiez vos informations personnelles'
              : 'View and edit your personal information'}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Profile Card */}
          <div className="lg:col-span-1">
            <UserProfileCard 
              key={profileKey}
              userId={user.user_id}
              language={language}
              showStats={true}
              showActions={true}
            />
          </div>

          {/* Right Column - Edit Forms */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  {language === 'fr' ? 'Informations du Compte' : 'Account Information'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="personal">
                  <TabsList className="mb-4">
                    <TabsTrigger value="personal">
                      {language === 'fr' ? 'Personnel' : 'Personal'}
                    </TabsTrigger>
                    <TabsTrigger value="security">
                      {language === 'fr' ? 'Sécurité' : 'Security'}
                    </TabsTrigger>
                    <TabsTrigger value="activity">
                      {language === 'fr' ? 'Activité' : 'Activity'}
                    </TabsTrigger>
                  </TabsList>

                  {/* Personal Info Tab */}
                  <TabsContent value="personal" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          {language === 'fr' ? 'Nom complet' : 'Full Name'}
                        </Label>
                        <Input
                          value={editForm.name}
                          onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          {language === 'fr' ? 'Email' : 'Email'}
                        </Label>
                        <Input value={user.email} disabled className="bg-muted" />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          {language === 'fr' ? 'Téléphone' : 'Phone'}
                        </Label>
                        <Input
                          value={editForm.phone_number}
                          onChange={(e) => setEditForm({...editForm, phone_number: e.target.value})}
                          placeholder="+243 81 XXX XXXX"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {language === 'fr' ? 'Date de naissance' : 'Date of Birth'}
                        </Label>
                        <Input
                          type="date"
                          value={editForm.date_of_birth}
                          onChange={(e) => setEditForm({...editForm, date_of_birth: e.target.value})}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          {language === 'fr' ? 'N° Carte d\'identité' : 'National ID'}
                        </Label>
                        <Input
                          value={editForm.national_id}
                          onChange={(e) => setEditForm({...editForm, national_id: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          {language === 'fr' ? 'Adresse' : 'Address'}
                        </Label>
                        <Input
                          value={editForm.address}
                          onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                        />
                      </div>
                    </div>

                    {/* Read-only professional info */}
                    {user.role !== 'CITIZEN' && (
                      <>
                        <Separator className="my-4" />
                        <h4 className="font-semibold text-sm text-muted-foreground flex items-center gap-2">
                          <Shield className="w-4 h-4" />
                          {language === 'fr' ? 'Informations Professionnelles (lecture seule)' : 'Professional Info (read-only)'}
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>{language === 'fr' ? 'Rôle' : 'Role'}</Label>
                            <Input value={user.role} disabled className="bg-muted" />
                          </div>
                          <div className="space-y-2">
                            <Label>{language === 'fr' ? 'Grade' : 'Rank'}</Label>
                            <Input value={user.grade || '-'} disabled className="bg-muted" />
                          </div>
                          <div className="space-y-2">
                            <Label>{language === 'fr' ? 'Matricule' : 'Badge Number'}</Label>
                            <Input value={user.matricule || '-'} disabled className="bg-muted" />
                          </div>
                          <div className="space-y-2">
                            <Label>{language === 'fr' ? 'Niveau d\'accès' : 'Clearance Level'}</Label>
                            <Input value={user.clearance_level || 'PUBLIC'} disabled className="bg-muted" />
                          </div>
                        </div>
                      </>
                    )}

                    <div className="flex justify-end pt-4">
                      <Button onClick={handleUpdateProfile} disabled={loading} className="gap-2">
                        <Save className="w-4 h-4" />
                        {language === 'fr' ? 'Enregistrer' : 'Save'}
                      </Button>
                    </div>
                  </TabsContent>

                  {/* Security Tab */}
                  <TabsContent value="security" className="space-y-4">
                    <div className="space-y-4 max-w-md">
                      <h4 className="font-semibold flex items-center gap-2">
                        <Lock className="w-4 h-4" />
                        {language === 'fr' ? 'Changer le mot de passe' : 'Change Password'}
                      </h4>

                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Mot de passe actuel' : 'Current Password'}</Label>
                        <div className="relative">
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            value={passwordForm.current_password}
                            onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Nouveau mot de passe' : 'New Password'}</Label>
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={passwordForm.new_password}
                          onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Confirmer le mot de passe' : 'Confirm Password'}</Label>
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={passwordForm.confirm_password}
                          onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                        />
                      </div>

                      <Button 
                        onClick={handleChangePassword} 
                        disabled={loading || !passwordForm.current_password || !passwordForm.new_password}
                        className="gap-2"
                      >
                        <Lock className="w-4 h-4" />
                        {language === 'fr' ? 'Changer' : 'Change'}
                      </Button>
                    </div>
                  </TabsContent>

                  {/* Activity Tab */}
                  <TabsContent value="activity" className="space-y-4">
                    <h4 className="font-semibold flex items-center gap-2">
                      <Activity className="w-4 h-4" />
                      {language === 'fr' ? 'Activité récente' : 'Recent Activity'}
                    </h4>

                    {activities.length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">
                        {language === 'fr' ? 'Aucune activité récente' : 'No recent activity'}
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {activities.map((activity, idx) => (
                          <div 
                            key={idx}
                            className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                          >
                            <div>
                              <p className="font-medium text-sm">{activity.action}</p>
                              <p className="text-xs text-muted-foreground">{activity.details}</p>
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {activity.timestamp ? new Date(activity.timestamp).toLocaleString('fr-FR') : '-'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MyProfilePage;
